import { request } from './client'
import { asObject, bool, str, text } from './parse'

/**
 * What the system is allowed to do on its own, and whether it is allowed to
 * speak at all.
 *
 *   GET  /api/settings           settings.read
 *   POST /api/settings/outbound  settings.write
 *   POST /api/settings/mode      settings.write
 *
 * The kill switch is the half that matters most, because it moves on its own:
 * the sender drops it when the Telegram session is revoked, when a flood wait
 * runs long, and when the daily send limit is reached. Until this screen existed
 * there was nowhere to put it back, and an owner whose account had hiccuped
 * could not answer a single client — the panel said "одобрено, отправка
 * поставлена в очередь" and the job quietly retried for ever.
 */
export interface Settings {
  readonly outboundEnabled: boolean
  /** Why it is off, in whoever's or whatever's words. Empty when it is on. */
  readonly outboundReason: string
  /** The server's own sentence, in English. Kept for logs and for anything that
   * is not this panel; the screen writes its own from the source and the reason,
   * because a server that composes what a person reads has chosen their language
   * for them. */
  readonly outboundExplanation: string
  /** Who last moved it: nobody ("configuration"), a person, or the sender
   * itself on a lost session, a long flood wait or the day's send limit. */
  readonly outboundSource: 'configuration' | 'person' | 'system'
  readonly mode: string
  readonly modeReason: string
  readonly modeExplanation: string
}

function settingSource(read: string): 'configuration' | 'person' | 'system' {
  return read === 'person' || read === 'system' ? read : 'configuration'
}

function settings(body: unknown): Settings {
  const source = asObject(body, 'the settings')
  return {
    outboundEnabled: bool(source, 'outbound_enabled'),
    outboundReason: text(source, 'outbound_reason'),
    outboundExplanation: text(source, 'outbound_explanation'),
    outboundSource: settingSource(text(source, 'outbound_source')),
    mode: str(source, 'mode'),
    modeReason: text(source, 'mode_reason'),
    modeExplanation: text(source, 'mode_explanation'),
  }
}

export async function fetchSettings(signal?: AbortSignal): Promise<Settings> {
  return request('/api/settings', settings, signal === undefined ? {} : { signal })
}

/**
 * Moves the kill switch. Switching off requires a reason: a system that stopped
 * answering clients with no note is one somebody has to reverse-engineer.
 */
export async function setOutbound(enabled: boolean, reason: string): Promise<Settings> {
  return request('/api/settings/outbound', settings, {
    method: 'POST',
    body: { enabled, reason },
  })
}

export async function setMode(mode: string, reason: string): Promise<Settings> {
  return request('/api/settings/mode', settings, { method: 'POST', body: { mode, reason } })
}
